import {Component, OnInit, OnDestroy, HostListener} from '@angular/core';
import {FormBuilder, FormControl} from '@angular/forms';
import Vendor from '../../../../../services/vendor.model';
import {VendorsService} from '../../../../../services/vendors.service';
import {BusinessEntityService} from '../../../../../services/businessEntity.service';
import BusinessEntity from 'src/app/services/businessEntity.model';
import {Sidenavaction} from '../../../../../../assets/js/application/sidenavaction';
import {ToastrService} from 'ngx-toastr';
import {CommonMethodsService} from 'src/app/services/commonMethods.service';
import {LocationStrategy} from '@angular/common';
import {PreviousRouteService} from 'src/app/services/previousRouteService';
import {BatchLoader} from "../../../../../Utils/batch-loader.util";

declare var $: any;

/**
 * VendorMasterComponent
 *
 * This component is responsible for managing Vendor Master data within the Material Management module.
 * It provides a user interface for administrators and farm managers to view, create, update, and search for vendors.
 * The component implements infinite scrolling for efficient data display and uses reactive forms for data entry.
 *
 * Key Responsibilities:
 * - Fetch and display a list of all vendors or vendors specific to a business.
 * - Provide search functionality to filter vendors by name, site, city, state, or contact.
 * - Manage the creation of new vendors with validation.
 * - Handle updates to existing vendor details.
 * - Implement infinite scrolling using BatchLoader to handle large datasets.
 * - Manage UI state for loading indicators, form visibility, and submission status.
 * - Handle role-based access control for viewing and managing vendors.
 *
 * Features:
 * - **CRUD Operations**: Create and Update vendors.
 * - **Real-time Search**: Filter list by multiple vendor attributes.
 * - **Infinite Scroll**: Load data in batches to improve performance.
 * - **Form Validation**: Ensures vendor details follow specific patterns (alphanumeric, GST format, etc.).
 * - **Role-based Data Loading**: Loads vendors based on user role (Admin vs Farm Manager).
 *
 * Data Flow:
 * 1. Component initializes (`ngOnInit`).
 * 2. Fetches vendors from `VendorsService` based on user role.
 * 3. Initializes `BatchLoader` with the full dataset.
 * 4. User interactions (scroll, search, add/edit) trigger state updates and service calls.
 * 5. `addVendorMaster` and `updateVendorMaster` send data to backend and refresh the list on success.
 *
 * @component
 * @selector app-vendor-master
 * @example
 * <app-vendor-master></app-vendor-master>
 *
 * @implements {OnInit}
 * @implements {OnDestroy}
 * @author Generated by CompDoc
 * @version 1.0.0
 */
@Component({
  selector: 'app-vendor-master',
  templateUrl: './vendor-master.component.html',
  styleUrls: ['./vendor-master.component.scss']
})
export class VendorMasterComponent implements OnInit, OnDestroy {
  /**
   * Array holding all fetched vendors from the backend.
   * Acts as the source of truth for the list before filtering.
   * @type {Vendor[]}
   * @default []
   */
  public vendors: Vendor[] = [];

  /**
   * Array holding the currently displayed vendors.
   * This list is populated by the BatchLoader (subset of vendors or filteredVendors).
   * @type {Vendor[]}
   * @default []
   */
  public vendorsList: Vendor[] = [];

  /**
   * Model for the current vendor being added or edited.
   * Bound to the form for data manipulation.
   * @type {Vendor}
   */
  public vendor: Vendor = new Vendor();

  /**
   * Array holding vendors that match the current search criteria.
   * Used as the source for BatchLoader when filtering is active.
   * @type {Vendor[]}
   * @default []
   */
  public filteredVendors: Vendor[] = [];

  /**
   * Array of business entities available for selection.
   * @type {Array<BusinessEntity>}
   */
  public businessEntities: Array<BusinessEntity>;

  /**
   * Flag indicating if the form has been submitted.
   * Used to trigger validation error display in the UI.
   * @type {boolean}
   * @default false
   */
  public submitted = false;

  /**
   * Name of the logged-in user.
   * @type {string}
   */
  public loginUserName: string;

  /**
   * Business name of the logged-in user.
   * @type {string}
   */
  public loginBusinessName: string;

  /**
   * Business name associated with the logged-in user (duplicate/alias).
   * @type {string}
   */
  public loginUserBusinessName: string;

  /**
   * Business category of the logged-in user.
   * @type {string}
   */
  public loginUserBusinessCategory: string;

  /**
   * Role of the logged-in user (e.g., 'Farm Manager', 'Business Admin').
   * @type {string}
   */
  public loginPersonType: string;

  /**
   * Flag indicating if it's the first time setting details (used for initialization logic).
   * @type {boolean}
   */
  public setFirsttime: boolean;

  /**
   * Current action being performed on the form ('ADD' or 'EDIT').
   * @type {string}
   */
  public formAction: string;

  /**
   * Flag to hide business details block in the UI.
   * @type {boolean}
   */
  public hideBusinessDetailsBlock: boolean;

  /**
   * Current search query string entered by the user.
   * @type {string}
   */
  public search: string;

  /**
   * Keyword used for filtering/searching (e.g., 'businessName').
   * @type {string}
   */
  public keyword: string;

  /**
   * Generic item property used for binding in templates.
   * @type {any}
   */
  public item: any;

  /**
   * Flag to prevent double clicks on add vendor action.
   * @type {boolean}
   */
  public addVendorDoubleClick: boolean;

  /**
   * Flag indicating if no data is available for display.
   * @type {boolean}
   * @default false
   */
  public NoDataMessage: boolean = false;

  /**
   * Flag indicating if a search has been executed.
   * @type {boolean}
   * @default false
   */
  public isSearchedClicked: boolean = false;

  /**
   * Flag to trigger clearing of the search input field.
   * @type {boolean}
   */
  public clearSearchInput: boolean;

  /**
   * Flag to control the visibility of the main page loader.
   * @type {boolean}
   */
  public responseLoader: boolean;

  /**
   * Flag to control the visibility of the list loader spinner during infinite scroll.
   * @type {boolean}
   * @default false
   */
  public listLoader = false;              // Show listLoader spinner

  /**
   * Tracks the number of items currently loaded by the batch loader.
   * @type {number}
   * @default 0
   */
  public startIndex = 0;             // Tracks how much has been loaded

  /**
   * Flag indicating if a search filter is currently active.
   * @type {boolean}
   * @default false
   */
  public isFiltering: boolean = false;

  /**
   * Flag indicating if the list is empty (no data or no search results).
   * @type {boolean}
   * @default false
   */
  public noList: boolean = false;

  /**
   * Constructor for VendorMasterComponent
   *
   * Initializes the component, injects required services, and sets up the vendor model.
   *
   * @constructor
   * @param {ToastrService} toasterService - Service for displaying toast notifications.
   * @service ToastrService
   * @param {VendorsService} vendorsService - Service for Vendor API operations.
   * @service VendorsService
   * @param {BusinessEntityService} businessEntityService - Service for Business Entity API operations.
   * @service BusinessEntityService
   * @param {Sidenavaction} sidenavaction - Service for side navigation actions.
   * @service Sidenavaction
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @service FormBuilder
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods and error handling.
   * @service CommonMethodsService
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @service LocationStrategy
   * @param {PreviousRouteService} previousRouteService - Service for tracking navigation history.
   * @service PreviousRouteService
   */
  constructor(private toasterService: ToastrService, public vendorsService: VendorsService, public businessEntityService: BusinessEntityService, public sidenavaction: Sidenavaction, public formBuilder: FormBuilder, public commonMethodsService: CommonMethodsService,
              private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
    this.vendor = new Vendor();
  }

  /**
   * Utility for handling batch loading of data for infinite scroll.
   * @type {BatchLoader<any>}
   * @private
   */
  private batchLoader: BatchLoader<any>;

  /**
   * Angular lifecycle hook - Component initialization
   *
   * Performs the following initialization steps:
   * 1. Resets component state and form.
   * 2. Tracks the current route.
   * 3. Retrieves user details from local storage.
   * 4. Fetches vendors based on user role (Admin vs Farm Manager).
   * 5. Loads business entities if the user is an admin.
   * 6. Initializes the batch loader.
   *
   * @async
   * @method ngOnInit
   * @returns {Promise<void>}
   * @lifecycle Angular component initialization hook
   */
  public async ngOnInit() {
    try {
      window.scrollTo(0, 0);
      this.vendors = [];
      this.vendorsList = [];
      this.filteredVendors = [];
      this.isFiltering = false;
      this.search = '';
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      // loginperson type details
      this.responseLoader = false;
      this.clearSearchInput = false;
      this.addVendorDoubleClick = false;
      this.loginPersonType = localStorage.getItem('loginPersonType');
      this.loginUserName = localStorage.getItem('loginUserName');
      this.loginUserBusinessName = localStorage.getItem('loginBusinessName');
      this.loginUserBusinessCategory = localStorage.getItem('loginBusinessCategory');
      this.hideBusinessDetailsBlock = false;
      this.keyword = 'businessName';
      /*end users data*/
      if (this.loginPersonType != 'Farm Manager' && this.loginPersonType != 'Farmer' && this.loginPersonType != 'Business Admin' && this.loginUserBusinessCategory != 'Farm') {
        this.getVendors();
        let farmBusiness = await this.businessEntityService.getAllBusiness();
        this.businessEntities = farmBusiness.filter((businessEntity: BusinessEntity) => businessEntity.businessType && businessEntity.businessType.length > 0 && businessEntity.businessType[0].businessTypeID && businessEntity.businessType[0].businessTypeID === '618916f2b4391c6d48137e3d');
      } else {
        this.getVendors();
      }
      this.formAction = 'ADD';
      this.isSearchedClicked = false;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * Fetches vendors from the backend based on user role.
   *
   * - If user is Admin/Tech Support: Fetches all vendors and filters out specific business types.
   * - If user is Farm Manager/Farmer: Fetches vendors specific to their business ID.
   *
   * @async
   * @method getVendors
   * @returns {Promise<void>}
   */
  public async getVendors() {
    if (this.loginPersonType != 'Farm Manager' && this.loginPersonType != 'Farmer' && this.loginPersonType != 'Business Admin' && this.loginUserBusinessCategory != 'Farm') {
      this.vendors = await this.vendorsService.getAllVendors();
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader?.reset();     // Clear state
      await this.batchLoader?.loadNextBatch(true); // Load first batch
      this.responseLoader = true;
      this.vendors = this.vendors.filter(vendor => vendor.business[0] && vendor.business[0].businessType && vendor.business[0].businessType[0].businessTypeID != '618a18ac0f339c001e991b08');
    } else {
      this.vendors = await this.vendorsService.getVendorsByBusinessId(localStorage.businessID);
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader?.reset();     // Clear state
      await this.batchLoader?.loadNextBatch(true); // Load first batch
      this.responseLoader = true;
    }
  }

  /**
   * Host listener for window scroll events.
   * Triggers the check for loading the next batch of data when scrolling near the bottom.
   *
   * @method onScroll
   * @returns {void}
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  /**
   * Timeout reference for scroll event debouncing.
   * @type {any}
   * @private
   */
  private scrollTimeout: any;

  /**
   * Checks scroll position and loads next batch if near bottom.
   * Debounced to prevent excessive calls.
   *
   * @method checkScrollAndLoad
   * @private
   * @returns {void}
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader?.isLoading()) {
        this.batchLoader.loadNextBatch();
      }
    }, 50);
  }

  /**
   * Initializes the BatchLoader instance.
   * Configures the source list (filtered or full) and the callback to update the displayed list.
   *
   * @method initializeBatchLoader
   * @private
   * @returns {void}
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.filteredVendors : this.vendors,
      (updatedList) => {
        this.vendorsList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  /**
   * Angular lifecycle hook - Component destruction
   *
   * Cleans up resources:
   * - Cancels ongoing HTTP requests.
   * - Destroys and resets the batch loader.
   *
   * @method ngOnDestroy
   * @returns {void}
   * @lifecycle Angular component destruction hook
   */
  public ngOnDestroy(): void {
    this.vendorsService.cancelOnGoingHttpRequests();
    this.businessEntityService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader?.reset();
  }

  /**
   * Handles form submission based on the current action (ADD or EDIT).
   *
   * @method vendorMasterFormSubmit
   * @returns {void}
   */
  public vendorMasterFormSubmit() {
    if (this.formAction == 'ADD') {
      this.addVendorMaster();
    } else if (this.formAction == 'EDIT') {
      this.updateVendorMaster();
    }
  }

  /**
   * Prepares the form for adding a new vendor.
   * Resets form fields and sets the action to 'ADD'.
   *
   * @method addVendorMasterIcon
   * @returns {void}
   */
  public addVendorMasterIcon() {
    $('tr').removeClass('rowactive');
    this.formAction = 'ADD';
    this.vendor.name = '';
    this.vendor.site = '';
    this.vendor.city = '';
    this.vendor.state = '';
    this.vendor.contact = '';
    this.vendor.GST_No = '';
    this.vendor.postal_code = '';
    this.vendor.comment = '';
    this.vendor.business_id = undefined;
    this.item = undefined;
    this.vendor.id = '';
  }

  /**
   * Populates the form with details of the selected vendor for viewing or editing.
   *
   * @method setVendorMasterDetails
   * @param {Vendor} currData - The vendor object to display.
   * @returns {void}
   */
  public setVendorMasterDetails(currData: Vendor) {
    this.setFirsttime = true;
    this.vendor.id = currData.id;
    this.vendor.name = currData.name;
    this.vendor.site = currData.site;
    this.vendor.city = currData.city;
    this.vendor.state = currData.state;
    this.vendor.contact = currData.contact;
    this.vendor.GST_No = currData.GST_No;
    this.vendor.postal_code = currData.postal_code;
    this.vendor.business_id = currData.business_id;
    const businessEntity = this.businessEntities.find(businessEntity => businessEntity && businessEntity.id === currData.business_id);
    this.item = businessEntity.businessName;
    this.businessCtrl.setValue(businessEntity);

  }

  /**
   * Prepares the form for editing an existing vendor.
   * Sets the action to 'EDIT' and populates the form.
   *
   * @method editVendormasterIcon
   * @param {Vendor} currData - The vendor object to edit.
   * @returns {void}
   */
  public editVendormasterIcon(currData: Vendor) {
    this.formAction = 'EDIT';
    this.addVendorDoubleClick = false;
    this.setVendorMasterDetails(currData);
    $('tr').removeClass('rowactive');
    $(event.target).parents('tr').addClass('rowactive');

  }

  /**
   * Handles the creation of a new vendor.
   * Validates the form data and calls the service to create the vendor.
   * Refreshes the vendor list upon success.
   *
   * @async
   * @method addVendorMaster
   * @returns {Promise<boolean | void>}
   * @throws {Error} If the service call fails.
   */
  public async addVendorMaster() {
    $('tr').removeClass('rowactive');
    if (!this.vendor.name) {
      this.toasterService.error('Please enter vendor name');
      return false;
    }
    if (this.vendor.name && this.vendor.name.length > 30) {
      this.toasterService.error('error', 'Maximum 30 characters allowed for vendor name');
      return false;
    }
    if (this.vendor.name && this.vendor.name.length < 3) {
      this.toasterService.error('error', 'Minimum 3 characters required for vendor name');
      return false;
    }
    if (this.vendor.site && this.vendor.site.length > 20) {
      this.toasterService.error('Maximum 20 characters allowed for vendor site');
      return false;
    }
    if (this.vendor.site && this.vendor.site.length < 3) {
      this.toasterService.error('Minimum 3 characters required for vendor site');
      return false;
    }
    if (!this.vendor.site) {
      this.toasterService.error('Please enter vendor site');
      return false;
    }
    if ((this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Farmer' || this.loginPersonType == 'Business Admin') && this.loginUserBusinessCategory == 'Farm') {
      this.vendor.business_id = localStorage.businessID;
    }
    if (!this.vendor.business_id) {
      this.toasterService.error('Please select business');
      return false;
    }
    if (this.vendor.city && this.vendor.city.length > 20) {
      this.toasterService.error('error', 'Maximum 20 characters allowed for city');
      return;
    }
    if (this.vendor.city && this.vendor.city.length < 3) {
      this.toasterService.error('error', 'Minimum 3 characters required for city');
      return;
    }
    if (this.vendor.state && this.vendor.state.length > 20) {
      this.toasterService.error('error', 'Maximum 20 characters allowed for state');
      return false;
    }
    if (this.vendor.state && this.vendor.state.length < 3) {
      this.toasterService.error('error', 'Minimum 3 characters required for state');
      return false;
    }
    if (this.vendor.postal_code && (this.vendor.postal_code.length < 6 || this.vendor.postal_code.length != 6)) {
      this.toasterService.error('Enter valid pincode', '', {
        timeOut: 2000
      });
      return false;
    }
    if (this.vendor.contact && (this.vendor.contact.length < 10 || this.vendor.contact.length != 10)) {
      this.toasterService.error('Enter valid mobile number', '', {
        timeOut: 2000
      });
      return false;
    }
    if (this.vendor.GST_No != undefined && this.vendor.GST_No != null && this.vendor.GST_No != '') {
      let regTest = /\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}/.test(this.vendor.GST_No);
      if (!regTest) {
        this.toasterService.error('Please enter valid GST No.');
        return false;
      }
    }
    try {
      this.addVendorDoubleClick = true;
      this.responseLoader = false;
      if (localStorage.loginPersonType != 'EMPLOYEE') {
        delete this.vendor.business_id;
      }
      await this.vendorsService.create(this.vendor);
      this.toasterService.success('Vendor Master created successfully', '', {
        timeOut: 3000
      });
      this.vendors = [];
      this.item = undefined;
      $('#newrecordid').trigger('click');
      this.businessCtrl.setValue(null);
      await this.getVendors();
      this.vendor = new Vendor();
      this.addVendorDoubleClick = false;
      this.formAction = 'ADD';
      this.vendor = new Vendor();
      this.vendor.business_id = '';
      this.responseLoader = true;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.addVendorDoubleClick = false;
    }
  }

  /**
   * Handles the update of an existing vendor.
   * Validates the form data and calls the service to update the vendor.
   * Refreshes the vendor list upon success.
   *
   * @async
   * @method updateVendorMaster
   * @returns {Promise<boolean | void>}
   * @throws {Error} If the service call fails.
   */
  public async updateVendorMaster() {
    if (this.vendor.name == undefined || this.vendor.name == '') {
      this.toasterService.error('Please enter vendor name');
      return false;
    }
    if (this.vendor.site == undefined || this.vendor.site == '') {
      this.toasterService.error('Please enter vendor site');
      return false;
    }
    if (this.vendor.name && this.vendor.name.length > 30) {
      this.toasterService.error('Maximum 30 characters allowed for vendor name');
      return false;
    }
    if (this.vendor.name && this.vendor.name.length < 3) {
      this.toasterService.error('Minimum 3 characters required for vendor name');
      return false;
    }
    if (this.vendor.site && this.vendor.site.length > 20) {
      this.toasterService.error('Maximum 20 characters allowed for vendor site');
      return false;
    }
    if (this.vendor.site && this.vendor.site.length < 3) {
      this.toasterService.error('Minimum 3 characters required for vendor site');
      return false;
    }
    if (this.vendor.city && this.vendor.city.length > 20) {
      this.toasterService.error('error', 'Maximum 20 characters allowed for city');
      return;
    }
    if (this.vendor.city && this.vendor.city.length < 3) {
      this.toasterService.error('error', 'Minimum 3 characters required for city');
      return;
    }
    if (this.vendor.state && this.vendor.state.length > 20) {
      this.toasterService.error('error', 'Maximum 20 characters allowed for state');
      return false;
    }
    if (this.vendor.state && this.vendor.state.length < 3) {
      this.toasterService.error('error', 'Minimum 3 characters required for state');
      return false;
    }
    if ((this.loginPersonType == 'Farm Manager' || this.loginPersonType == 'Farmer' || this.loginPersonType == 'Business Admin') && this.loginUserBusinessCategory == 'Farm') {
      this.vendor.business_id = localStorage.businessID;
    }
    if (!this.vendor.business_id) {
      this.toasterService.error('Please select business');
      return false;
    }
    if (this.vendor.postal_code && (this.vendor.postal_code.length < 6 || this.vendor.postal_code.length != 6)) {
      this.toasterService.error('Enter valid pincode', '', {
        timeOut: 2000
      });
      return false;
    }
    if (this.vendor.contact && (this.vendor.contact.length < 10 || this.vendor.contact.length != 10)) {
      this.toasterService.error('Enter valid mobile number', '', {
        timeOut: 2000
      });
      return false;
    }
    if (this.vendor.GST_No != undefined && this.vendor.GST_No != null && this.vendor.GST_No != '') {
      let regTest = /\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z\d]{1}[Z]{1}[A-Z\d]{1}/.test(this.vendor.GST_No);
      if (!regTest) {
        this.toasterService.error('Please enter valid GST No.');
        return false;
      }
    }
    try {
      this.addVendorDoubleClick = true;
      this.responseLoader = false;
      const updateVendorMaster = Object.assign({}, this.vendor);
      delete updateVendorMaster.id;
      if (localStorage.loginPersonType != 'EMPLOYEE') {
        delete updateVendorMaster.business_id;
      }
      await this.vendorsService.updateOneByID(this.vendor.id, updateVendorMaster);
      this.toasterService.success('Vendor master Updated successfully', '', {
        timeOut: 3000
      });
      this.vendor = new Vendor();
      this.vendors = [];
      this.item = undefined;
      $('#newrecordid').trigger('click');
      this.businessCtrl.setValue(null);
      await this.getVendors();
      this.addVendorDoubleClick = false;
      this.formAction = 'ADD';
      this.responseLoader = true;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.addVendorDoubleClick = false;
    }
  }

  /**
   * Filters the vendor list based on the search query.
   *
   * - Filters by name, site, city, state, contact, or business name (case-insensitive).
   * - Updates `filteredVendors`.
   * - Resets pagination and loads the first batch of filtered results.
   *
   * @method applySearch
   * @returns {void}
   * @side-effect Updates `filteredVendors`, `vendorsList`, `startIndex`, `noList`, `isFiltering`.
   */
  public applySearch(): void {
    const query = this.search?.trim().toLowerCase() || '';
    this.isFiltering = query.length > 0;
    const sourceList = this.isFiltering
      ? this.vendors.filter(vendor =>
        vendor.name?.toLowerCase().includes(query) ||
        vendor.site?.toLowerCase().includes(query) ||
        vendor.city?.toLowerCase().includes(query) ||
        vendor.state?.toLowerCase().includes(query) ||
        vendor.contact?.toLowerCase().includes(query) ||
        vendor.business?.[0]?.businessName?.toLowerCase().includes(query)
      )
      : this.vendors;
    this.filteredVendors = this.isFiltering ? sourceList : [];
    // Reset pagination and load first batch only
    this.startIndex = 0;
    const firstBatch = sourceList.slice(0, this.batchLoader?.recordsSize);
    this.vendorsList = structuredClone(firstBatch);
    this.startIndex = firstBatch.length;
    this.noList = this.vendorsList.length === 0;
  }

  /**
   * Validates input to allow only alphabets, spaces, and dots.
   * Prevents spaces as the first character and consecutive dots.
   *
   * @method alphabetsValidation
   * @param {any} event - The keyboard event.
   * @returns {void}
   */
  public alphabetsValidation(event: any) {
    const key = event.key;
    const value = event.target.value;
    // Disallow spaces as the first character
    if (key === ' ' && value === '') {
      event.preventDefault();
    } else if (key === '.' && value.endsWith('')) {
      event.preventDefault(); // Disallow dot after a word or space
    } else {
      // Allow alphabets, spaces, the dot (decimal point), and the Backspace key
      if (
        !/^[A-Za-z\s]$/.test(key) && // Check if the entered character is an alphabet or space
        key !== '.' && // Check if the entered character is a dot (decimal point)
        key !== 'Backspace' // Check if the Backspace key was pressed
      ) {
        event.preventDefault();
      }
      // Allow only one dot (decimal point)
      if (key === '.' && value.includes('.')) {
        event.preventDefault();
      }
      // Disallow the first letter being a dot
      if (key === '.' && value === '') {
        event.preventDefault();
      }
    }
  }

  /**
   * Validates input to allow only numbers.
   *
   * @method numberValidation
   * @param {any} event - The keyboard event.
   * @returns {void}
   */
  public numberValidation(event: any) {
    const key = event.key;
    const value = event.target.value;
    // Allow numbers and the Backspace key
    if (
      !/^\d$/.test(key) && // Check if the entered character is a number
      key !== 'Backspace' // Check if the Backspace key was pressed
    ) {
      event.preventDefault();
    }
    // Disallow the first letter being a dot
    if (key === '.' && value === '') {
      event.preventDefault();
    }
  }

    /**
     * Form control for business selection (reactive forms).
     * @type {FormControl}
     */
    businessCtrl = new FormControl<string | any>('');
    
    /**
     * Displays the business name in the autocomplete input.
     *
     * @method displayBusiness
     * @param {any | string | null} business - The business object or string.
     * @returns {string} The business name.
     */
    public displayBusiness(business: any | string | null): string {
      return business.businessName;
    }
    
    /**
     * Filtered list of businesses for autocomplete.
     * @type {any}
     */
    public filteredBusinesses: any = [];

    /**
     * Filters the business list based on user input.
     *
     * @method filterBusinesses
     * @param {KeyboardEvent} [event] - The keyboard event (optional).
     */
    public filterBusinesses(event?: KeyboardEvent): void {
      const value = (event
          ? (event.target as HTMLInputElement).value
          : this.businessCtrl.value) || '';
    
      const search = typeof value === 'string' ? value.toLowerCase()
                                                : value?.name.toLowerCase();
    
      this.filteredBusinesses = this.businessEntities
          ?.filter(p => p.businessName.toLowerCase().includes(search)) ?? [];
    }
  
    /**
     * Handles the selection of a business from the autocomplete list.
     * Sets the selected business in the form control and updates the vendor model.
     *
     * @method selectOption
     * @param {BusinessEntity} business - The selected business entity.
     */
    public selectOption(business: BusinessEntity): void {
      this.businessCtrl.setValue(business);
      this.vendor.business_id = business.id;
    }
}
